export type AssociationTab = string;

export type SummaryMatchStats = {
  evidenceRowCount: number;
  summaryRowCount: number;
  matchedSummaryCount: number;
  uniqueAssociationCount: number;
};

function normalizeCui(value: string | undefined): string {
  return String(value ?? '')
    .trim()
    .replace(/^UMLS_/i, '')
    .toUpperCase();
}

function pickField(row: Record<string, string>, keys: string[]): string {
  for (const key of keys) {
    const value = row[key];
    if (value != null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }
  return '';
}

export function buildAssociationKey(
  row: Record<string, string>,
  tab: AssociationTab,
): string | null {
  const normalizedTab = String(tab).toUpperCase();
  const disease = normalizeCui(
    pickField(row, ['diseaseUMLSCUI', 'disease', 'disease_name', 'diseaseName']),
  );
  if (!disease) return null;

  if (normalizedTab === 'GDA') {
    const gene = pickField(row, ['geneSymbol', 'gene_symbol', 'symbol']).toUpperCase();
    if (!gene) return null;
    return `gda:${gene}|${disease}`;
  }

  if (normalizedTab === 'VDA') {
    const variant = pickField(row, ['variantStrID', 'variant_name', 'strID', 'strId']);
    if (!variant) return null;
    return `vda:${variant}|${disease}`;
  }

  return null;
}

export function collectAssociationKeys(
  rows: Record<string, string>[],
  tab: AssociationTab,
): Set<string> {
  const keys = new Set<string>();
  rows.forEach(row => {
    const key = buildAssociationKey(row, tab);
    if (key) keys.add(key);
  });
  return keys;
}

export function matchEvidenceToSummary(
  evidenceRows: Record<string, string>[],
  summaryRows: Record<string, string>[],
  tab: AssociationTab,
): { matchedRows: Record<string, string>[]; stats: SummaryMatchStats } {
  const associationKeys = collectAssociationKeys(evidenceRows, tab);

  const matchedRows = summaryRows.filter(row => {
    const key = buildAssociationKey(row, tab);
    return key != null && associationKeys.has(key);
  });

  return {
    matchedRows,
    stats: {
      evidenceRowCount: evidenceRows.length,
      summaryRowCount: summaryRows.length,
      matchedSummaryCount: matchedRows.length,
      uniqueAssociationCount: associationKeys.size,
    },
  };
}

export function getJoinKeyUiColumns(tab: AssociationTab): string[] {
  const normalizedTab = String(tab).toUpperCase();
  if (normalizedTab === 'VDA') {
    return ['variantStrID', 'diseaseUMLSCUI', 'diseaseName'];
  }
  return ['geneSymbol', 'diseaseUMLSCUI', 'diseaseName'];
}

export function withForcedDownloadColumns(
  columnVisibilityModel: Record<string, boolean> | undefined,
  forcedUiKeys: string[],
): Record<string, boolean> {
  const base = { ...(columnVisibilityModel ?? {}) };
  forcedUiKeys.forEach(key => {
    base[key] = true;
  });
  return base;
}
